import type {
  GetRequestByIdResponse,
  UpdateRequestById,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { requestKeys } from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest";
import apiClient from "@/lib/api/client";

export type MbcRequestSettingsPatch = Pick<
  UpdateRequestById,
  | "collaboratorIds"
  | "creativeDirection"
  | "preferredDesignerIds"
  | "restrictAiAssets"
>;

type MbcRequestSettings = {
  collaboratorIds: number[];
  creativeDirection: GetRequestByIdResponse["creativeDirection"];
  isLoading: boolean;
  preferredDesignerIds: number[];
  projectId: number | undefined;
  restrictAiAssets: boolean | undefined;
  save: (patch: MbcRequestSettingsPatch) => void;
};

function applyPatch(
  request: GetRequestByIdResponse,
  patch: MbcRequestSettingsPatch,
): GetRequestByIdResponse {
  const { collaboratorIds, ...rest } = patch;

  return {
    ...request,
    ...rest,
    ...(collaboratorIds && { collaboratorsIds: collaboratorIds }),
  };
}

/**
 * Reads and persists the request-row settings the review sidebar owns. The
 * edit endpoint routes `collaboratorIds` to its own handler, so each patch
 * must carry a single field.
 */
export function useMbcRequestSettings(
  requestId: null | number,
): MbcRequestSettings {
  const queryClient = useQueryClient();
  const { data: request, isLoading } = useGetRequest(requestId ?? undefined);
  const queryKey = requestKeys.requestById(requestId ?? 0);

  const { mutate } = useMutation<
    unknown,
    Error,
    MbcRequestSettingsPatch,
    { snapshot?: GetRequestByIdResponse }
  >({
    mutationFn: (patch: MbcRequestSettingsPatch) =>
      apiClient.url(`/requests/edit/${requestId}`).patch(patch).json(),
    onError: (error, _patch, previous) => {
      queryClient.setQueryData(queryKey, previous?.snapshot);
      toast.add({
        description: error.message,
        title: "Couldn't save your changes",
        type: "error",
      });
    },
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot =
        queryClient.getQueryData<GetRequestByIdResponse>(queryKey);

      if (snapshot) {
        queryClient.setQueryData(queryKey, applyPatch(snapshot, patch));
      }

      return { snapshot };
    },
  });

  return {
    collaboratorIds: request?.collaboratorsIds ?? [],
    creativeDirection: request?.creativeDirection ?? null,
    isLoading,
    preferredDesignerIds: request?.preferredDesignerIds ?? [],
    projectId: request?.projectId,
    restrictAiAssets: request?.restrictAiAssets,
    save: (patch: MbcRequestSettingsPatch) => {
      if (requestId !== null) mutate(patch);
    },
  };
}
